import React, { useMemo } from "react";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemText from "@material-ui/core/ListItemText";
import AccessTree, { BlueCheckbox, useStyles } from "../AccessTree/AccessTree";
import { emptyAccess, expandAreaIds } from "../../services/access/accessResolver";
import "./AccessAssignmentPanel.scss";

/**
 * Builds the region -> area -> business tree from the account's region and
 * business data, with `checked` flags set from the supplied `access` payload.
 * Region grants cascade area rows checked-on; area grants cascade business
 * rows checked-on. Mirrors the runtime resolver's region -> area cascade so
 * the UI stays consistent with what the resolver will actually grant.
 */
function buildOptions(regions, businesses, access, lockedBusinessIds = []) {
  const a = access || emptyAccess();
  const regionIds = new Set(a.regionIds || []);
  // Cascade region -> area like the resolver does, so checking a region marks
  // its child areas as checked in the tree.
  const areaIds = expandAreaIds(a.areaIds, a.regionIds, regions);
  const businessIds = new Set(a.businessIds || []);
  const lockedSet = new Set(lockedBusinessIds);

  const topLevel = regions.filter(r => !r.isArea);
  const areas = regions.filter(r => r.isArea);
  // "Without Region" group for areas without a parent region.
  const orphanAreas = areas.filter(a => !a.parentRegion);

  const buildAreaNode = area => {
    const areaChecked = areaIds.has(area.id);
    const subs = businesses
      .filter(b => b.regionId === area.id)
      .map(b => ({
        value: b.businessId,
        label: b.name,
        checked: areaChecked || businessIds.has(b.businessId) || lockedSet.has(b.businessId),
        locked: lockedSet.has(b.businessId)
      }));
    return {
      value: area.id,
      label: area.name,
      parentRegion: area.parentRegion,
      checked: areaChecked,
      subChildren: subs
    };
  };

  const regionNodes = topLevel.map(region => {
    const regionChecked = regionIds.has(region.id);
    const childAreas = areas.filter(a => a.parentRegion === region.id).map(buildAreaNode);
    return {
      value: region.id,
      label: region.name,
      checked: regionChecked,
      children: childAreas
    };
  });

  if (orphanAreas.length > 0) {
    regionNodes.push({
      value: "without-region",
      label: "Without Region",
      synthetic: true,
      checked: false,
      children: orphanAreas.map(buildAreaNode)
    });
  }
  // Orphan businesses (no area at all) live under their own synthetic group,
  // distinct from the orphan-areas group above so the two never collide on
  // `value` (tree lookups below key nodes by `value`).
  const noAreaBusinesses = businesses.filter(b => !b.regionId);
  if (noAreaBusinesses.length > 0) {
    regionNodes.push({
      value: "without-region-businesses",
      label: "Without Region",
      synthetic: true,
      checked: false,
      children: [
        {
          value: "without-area",
          label: "Without Area",
          parentRegion: "without-region-businesses",
          synthetic: true,
          checked: false,
          subChildren: noAreaBusinesses.map(b => ({
            value: b.businessId,
            label: b.name,
            checked: businessIds.has(b.businessId) || lockedSet.has(b.businessId),
            locked: lockedSet.has(b.businessId)
          }))
        }
      ]
    });
  }
  return regionNodes;
}

/**
 * Reads the checked state out of the option tree and produces an `access`
 * payload. Region rows that are fully checked contribute their id to
 * `regionIds` (unless they're a synthetic "without region/area" bucket);
 * area rows that are checked contribute `areaIds`, unless the parent region
 * is also checked (in which case the region grant already covers the area,
 * and persisting the area id too would bake in a stale explicit grant that
 * survives the area later moving to a different region); checked businesses
 * (that aren't already covered by their area) contribute `businessIds`.
 */
function readAccess(options, prevAccess, lockedBusinessIds = []) {
  const access = emptyAccess();
  access.allLocations = !!(prevAccess && prevAccess.allLocations);
  const lockedSet = new Set(lockedBusinessIds);

  for (const region of options) {
    if (!region.synthetic && region.checked) {
      access.regionIds.push(region.value);
    }
    for (const area of region.children || []) {
      if (!area.synthetic && area.checked && !region.checked) {
        access.areaIds.push(area.value);
      }
      for (const sub of area.subChildren || []) {
        if (sub.locked && !area.checked) {
          access.businessIds.push(sub.value);
        } else if (sub.checked && !area.checked) {
          access.businessIds.push(sub.value);
        }
      }
    }
  }
  // Dedupe in case a locked id was already captured.
  access.businessIds = Array.from(new Set(access.businessIds));
  return access;
}

const AccessAssignmentPanel = ({ access, onChange, regions, businesses, disabled, lockedBusinessIds = [] }) => {
  const classes = useStyles();
  const options = useMemo(
    () => buildOptions(regions || [], businesses || [], access, lockedBusinessIds),
    [regions, businesses, access, lockedBusinessIds]
  );

  // We keep `options` derived (read-only) and toggle by mutating a shallow
  // copy — same routine MultiSelectFilter used internally, lifted here.
  const clone = () =>
    options.map(r => ({
      ...r,
      children: (r.children || []).map(c => ({
        ...c,
        subChildren: (c.subChildren || []).map(sc => ({ ...sc }))
      }))
    }));

  const allLocations = !!(access && access.allLocations);

  const setRegionChecked = (region, state) => {
    const next = clone();
    const idx = next.findIndex(r => r.value === region.value);
    next[idx].checked = state;
    let areaIds = new Set();
    if (state) {
      // When checking a region, mark all descendant areas + businesses.
      next[idx].children = next[idx].children.map(c => ({
        ...c,
        checked: true,
        subChildren: (c.subChildren || []).map(sc => ({ ...sc, checked: true }))
      }));
    } else {
      next[idx].children = next[idx].children.map(c => ({
        ...c,
        checked: false,
        subChildren: (c.subChildren || []).map(sc => ({ ...sc, checked: sc.locked ? true : false }))
      }));
    }
    onChange(readAccess(next, access, lockedBusinessIds));
  };

  const setAreaChecked = (region, area, state) => {
    const next = clone();
    const rIdx = next.findIndex(r => r.value === region.value);
    const aIdx = next[rIdx].children.findIndex(c => c.value === area.value);
    next[rIdx].children[aIdx].checked = state;
    next[rIdx].children[aIdx].subChildren = (next[rIdx].children[aIdx].subChildren || []).map(sc => ({
      ...sc,
      // When unchecking an area, preserve locked sub-children as checked
      // so the primary location cannot be unchecked via the parent area.
      checked: !state && sc.locked ? true : state
    }));
    // Reflect parent region as indeterminate: when its children are
    // partially checked, we leave `region.checked = false` so the checkbox
    // shows indeterminate. When all children are checked, set region checked.
    let allChecked = next[rIdx].children.length > 0;
    let noneChecked = true;
    next[rIdx].children.forEach(c => {
      c.checked ? (noneChecked = false) : (allChecked = false);
    });
    next[rIdx].checked = allChecked;
    onChange(readAccess(next, access, lockedBusinessIds));
  };

  const setSubChildChecked = (region, area, sub) => {
    // Locked subs (e.g. the user's primary location) cannot be unchecked.
    if (sub.locked) return;
    const next = clone();
    const rIdx = next.findIndex(r => r.value === region.value);
    const aIdx = next[rIdx].children.findIndex(c => c.value === area.value);
    const sIdx = next[rIdx].children[aIdx].subChildren.findIndex(sc => sc.value === sub.value);
    next[rIdx].children[aIdx].subChildren[sIdx].checked = !next[rIdx].children[aIdx].subChildren[sIdx].checked;
    const subs = next[rIdx].children[aIdx].subChildren;
    const allSubs = subs.every(s => s.checked);
    const noSubs = subs.every(s => !s.checked);
    if (allSubs) {
      next[rIdx].children[aIdx].checked = true;
      // If every sibling area is now checked, mark region checked too.
      const allAreas = next[rIdx].children.every(c => c.checked);
      next[rIdx].checked = allAreas;
    } else if (noSubs) {
      next[rIdx].children[aIdx].checked = false;
      next[rIdx].checked = false;
    } else {
      next[rIdx].children[aIdx].checked = false;
      next[rIdx].checked = false;
    }
    onChange(readAccess(next, access, lockedBusinessIds));
  };

  // A region/area that contains the user's primary (locked) location always
  // reports that location as checked in `options`, regardless of whether the
  // rest of the group is selected (see buildAreaNode above). That breaks the
  // normal "anything checked? -> uncheck, else -> check" click direction for
  // those groups: the locked child's forced-true would always look like
  // "something's checked" and the click could never fully select the group.
  // For groups containing the primary location, decide direction off
  // "is everything checked?" instead: not-all-checked -> select all,
  // all-checked -> deselect all but the primary location.
  const areaContainsPrimary = area => (area.subChildren || []).some(sc => sc.locked);
  const areaAllChecked = area => {
    const subs = area.subChildren || [];
    return subs.length > 0 && subs.every(sc => sc.checked);
  };
  const regionContainsPrimary = region => (region.children || []).some(areaContainsPrimary);
  const regionAllChecked = region => {
    const subs = (region.children || []).flatMap(c => c.subChildren || []);
    return subs.length > 0 && subs.every(sc => sc.checked);
  };

  const toggleAllLocations = () => {
    const nextAccess = emptyAccess();
    nextAccess.allLocations = !allLocations;
    onChange(nextAccess);
  };

  return (
    <div className="access-assignment-panel">
      <div className="access-assignment-panel__left">
        <MenuItem className={classes.groupParent} onClick={toggleAllLocations} disabled={disabled}>
          <BlueCheckbox checked={allLocations} />
          <ListItemText primary="All locations (current + future)" />
        </MenuItem>
        <div
          style={{
            opacity: allLocations || disabled ? 0.5 : 1,
            pointerEvents: allLocations || disabled ? "none" : "auto"
          }}
        >
          <AccessTree
            options={options}
            classes={classes}
            lockedBusinessIds={lockedBusinessIds}
            onToggleParent={v => {
              if (regionContainsPrimary(v)) {
                return regionAllChecked(v) ? setRegionChecked(v, false) : setRegionChecked(v, true);
              }
              return v.checked || v.children.some(c => c.checked || (c.subChildren || []).some(sc => sc.checked))
                ? setRegionChecked(v, false)
                : setRegionChecked(v, true);
            }}
            onToggleChild={(v, c) => {
              if (areaContainsPrimary(c)) {
                return areaAllChecked(c) ? setAreaChecked(v, c, false) : setAreaChecked(v, c, true);
              }
              return c.checked || (c.subChildren || []).some(sc => sc.checked)
                ? setAreaChecked(v, c, false)
                : setAreaChecked(v, c, true);
            }}
            onToggleSubChild={(v, c, sc) => setSubChildChecked(v, c, sc)}
          />
        </div>
      </div>
    </div>
  );
};

export default AccessAssignmentPanel;
